import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { CalendarIcon, Scissors, Phone, ShieldCheck, Zap, PawPrint, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useLang } from "@/context/LanguageContext";
import { PHONE_1 } from "@/i18n/translations";
import api from "@/lib/api";
import { track } from "@/lib/analytics";
import { isDateDisabled, loadClosures } from "@/lib/hours";
import { normalizePhone, isValidGrPhone, isValidEmail } from "@/lib/phone";

export const GroomingBooking = () => {
  const { t } = useLang();
  const g = t.grooming;

  const [form, setForm] = useState({
    customer_name: "", surname: "", phone: "", email: "", pet_name: "", pet_breed: "",
    pet_size: "", service: "", preferred_time: "", notes: "",
  });
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [slots, setSlots] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponState, setCouponState] = useState({ status: "idle", label: null, reason: null });

  const checkCoupon = async () => {
    if (!couponCode.trim()) return;
    if (!form.phone) { toast.error(g.coupon_fill_phone); return; }
    setCouponState({ status: "checking", label: null, reason: null });
    try {
      const res = await api.get(`/coupons/validate?code=${encodeURIComponent(couponCode.trim())}&phone=${encodeURIComponent(form.phone)}&service=grooming`);
      if (res.data.valid) setCouponState({ status: "valid", label: res.data.label, reason: null });
      else setCouponState({ status: "invalid", label: null, reason: res.data.reason });
    } catch {
      setCouponState({ status: "invalid", label: null, reason: "Error" });
    }
  };
  const [, setClosuresTick] = useState(0);

  useEffect(() => {
    loadClosures().then(() => setClosuresTick((v) => v + 1));
  }, []);

  const loadSlots = useCallback(async (d) => {
    setLoadingSlots(true);
    try {
      const res = await api.get(`/grooming/availability?date=${format(d, "yyyy-MM-dd")}`);
      setSlots(res.data);
    } catch {
      setSlots({ available: [], all_slots: [], booked: [] });
    } finally {
      setLoadingSlots(false);
    }
  }, []);

  useEffect(() => {
    if (date) loadSlots(date);
    else setSlots(null);
  }, [date, loadSlots]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onPhoneChange = (e) => set("phone", normalizePhone(e.target.value).slice(0, 10));
  const phoneError = form.phone.length > 0 && !isValidGrPhone(form.phone);

  const onPickDate = (d) => {
    setDate(d);
    setForm((f) => ({ ...f, preferred_time: "" }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.pet_name || !form.pet_breed || !form.pet_size || !date || !form.preferred_time) {
      toast.error(g.required);
      return;
    }
    if (!isValidGrPhone(form.phone)) {
      toast.error(g.phone_error);
      return;
    }
    if (!isValidEmail(form.email)) {
      toast.error(g.email_error);
      return;
    }
    setLoading(true);
    try {
      const payload = {
        customer_name: form.surname.trim() ? `${form.customer_name.trim()} ${form.surname.trim()}` : form.customer_name.trim(),
        first_name: form.customer_name.trim(),
        phone: form.phone,
        email: form.email || null,
        pet_name: form.pet_name,
        pet_breed: form.pet_breed || null,
        pet_size: form.pet_size,
        service: form.service || null,
        preferred_date: format(date, "yyyy-MM-dd"),
        preferred_time: form.preferred_time,
        notes: form.notes || null,
        coupon_code: couponState.status === "valid" ? couponCode.trim().toUpperCase() : null,
      };
      await api.post("/grooming-requests", payload);
      track("grooming_booking", { fb_event: "Schedule", service: "grooming" });
      toast.success(g.success);
      setForm({ customer_name: "", surname: "", phone: "", email: "", pet_name: "", pet_breed: "", pet_size: "", service: "", preferred_time: "", notes: "" });
      setDate(null);
    } catch (err) {
      toast.error(g.error);
    } finally {
      setLoading(false);
    }
  };

  const field = "rounded-xl border-brand/20 bg-white focus-visible:ring-brand h-11";

  return (
    <section id="grooming" className="relative py-24 sm:py-32 bg-brand-soft/30" data-testid="grooming">
      {/* Decorative bg */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-white/60 to-transparent" />
      <div className="max-w-4xl mx-auto px-5 sm:px-8 relative">
        {/* BIG type badge — makes it unmistakably clear this is Grooming booking */}
        <div className="flex justify-center">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-3 rounded-full bg-brand text-brand-cream px-6 py-3 shadow-[0_12px_40px_rgba(134,77,99,0.25)]"
            data-testid="grooming-type-badge"
          >
            <span className="grid place-items-center h-9 w-9 rounded-full bg-brand-cream/20">
              <Scissors className="h-5 w-5" />
            </span>
            <span className="font-heading text-lg sm:text-xl font-bold tracking-wide uppercase">{g.tag}</span>
          </motion.div>
        </div>

        <div className="text-center max-w-2xl mx-auto mt-6">
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-brand-ink tracking-tight">{g.title}</h2>
          <p className="mt-4 text-brand-ink/70 leading-relaxed text-lg">{g.subtitle}</p>
        </div>

        {/* Trust indicators — reinforce this is instant online booking, not a callback */}
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-bamboo/25 px-4 py-1.5 text-sm font-bold text-bamboo-deep">
            <Zap className="h-3.5 w-3.5" /> {g.badge_instant}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-brand/15 px-4 py-1.5 text-sm font-bold text-brand">
            <ShieldCheck className="h-3.5 w-3.5" /> {g.badge_secure}
          </span>
        </div>

        <motion.form
          onSubmit={submit}
          data-form-name="grooming"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-10 bg-white rounded-[2rem] border border-brand/10 shadow-[0_16px_50px_rgba(134,77,99,0.1)] p-7 sm:p-10 grid sm:grid-cols-2 gap-5"
          data-testid="grooming-form"
        >
          <div>
            <Label className="text-brand-ink font-semibold">{g.customer_name} *</Label>
            <Input data-testid="g-name" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} className={field} />
          </div>
          <div>
            <Label className="text-brand-ink font-semibold">{g.surname}</Label>
            <Input data-testid="g-surname" value={form.surname} onChange={(e) => set("surname", e.target.value)} className={field} />
          </div>
          <div>
            <Label className="text-brand-ink font-semibold">{g.phone} *</Label>
            <Input
              data-testid="g-phone"
              value={form.phone}
              onChange={onPhoneChange}
              inputMode="tel"
              maxLength={10}
              placeholder="69XXXXXXXX"
              className={`${field} ${phoneError ? "border-rose-400 focus-visible:ring-rose-400" : ""}`}
            />
            <p className={`mt-1 text-xs ${phoneError ? "text-rose-600 font-semibold" : "text-brand-ink/50"}`} data-testid="g-phone-hint">
              {phoneError ? g.phone_error : g.phone_hint}
            </p>
          </div>
          <div>
            <Label className="text-brand-ink font-semibold">{g.email} *</Label>
            <Input data-testid="g-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={field} />
          </div>
          <div>
            <Label className="text-brand-ink font-semibold">{g.pet_name} *</Label>
            <Input data-testid="g-petname" value={form.pet_name} onChange={(e) => set("pet_name", e.target.value)} className={field} />
          </div>
          <div>
            <Label className="text-brand-ink font-semibold">{g.pet_breed} *</Label>
            <Input data-testid="g-breed" value={form.pet_breed} onChange={(e) => set("pet_breed", e.target.value)} className={field} />
          </div>
          <div>
            <Label className="text-brand-ink font-semibold">{g.pet_size} *</Label>
            <Select value={form.pet_size} onValueChange={(v) => set("pet_size", v)}>
              <SelectTrigger data-testid="g-size" className={field}><SelectValue placeholder={g.pick_size} /></SelectTrigger>
              <SelectContent>
                {Object.entries(g.sizes).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-brand-ink font-semibold">{g.date} *</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button data-testid="g-date" type="button" variant="outline" className={`${field} w-full justify-start font-normal no-a11y-underline ${!date && "text-muted-foreground"}`}>
                  <CalendarIcon className="h-4 w-4 mr-2 text-brand" />
                  {date ? format(date, "dd/MM/yyyy") : g.pick_date}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={date} onSelect={onPickDate} disabled={isDateDisabled} initialFocus />
              </PopoverContent>
            </Popover>
          </div>
          <div>
            <Label className="text-brand-ink font-semibold">{g.time} *</Label>
            <Select value={form.preferred_time} onValueChange={(v) => set("preferred_time", v)} disabled={!date || loadingSlots}>
              <SelectTrigger data-testid="g-time" className={field}><SelectValue placeholder={date ? (loadingSlots ? g.loading : g.pick_time) : g.pick_date} /></SelectTrigger>
              <SelectContent>
                {slots?.all_slots.length === 0 && date && !loadingSlots && <div className="px-3 py-2 text-sm text-brand-ink/50">{g.closed_day}</div>}
                {slots?.all_slots.map((tm) => {
                  const booked = slots.booked?.includes(tm);
                  const unavail = !slots.available?.includes(tm); // booked, ±2h window or <2h from now
                  return <SelectItem key={tm} value={tm} disabled={unavail}>{tm}{booked ? ` · ${g.time_taken}` : ""}</SelectItem>;
                })}
              </SelectContent>
            </Select>
            {date && format(date, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd") && (
              <p className="mt-2 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 font-semibold" data-testid="g-call-soon">
                {g.call_soon}{" "}
                <a href={`tel:${PHONE_1}`} onClick={() => track("click_phone", { phone: PHONE_1, source: "grooming-form-soon" })} className="underline inline-flex items-center gap-1">
                  <Phone className="h-3 w-3" /> {PHONE_1}
                </a>
              </p>
            )}
            <p className="mt-2 text-xs text-brand-ink/60 leading-relaxed">
              {g.call_more_slots}{" "}
              <a
                href={`tel:${PHONE_1}`}
                onClick={() => track("click_phone", { phone: PHONE_1, source: "grooming-form" })}
                className="font-bold text-brand hover:underline inline-flex items-center gap-1"
                data-testid="g-call-more-slots-link"
              >
                <Phone className="h-3 w-3" /> {PHONE_1}
              </a>
            </p>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-brand-ink font-semibold">{g.service_wanted}</Label>
            <Textarea
              data-testid="g-service"
              value={form.service}
              onChange={(e) => set("service", e.target.value)}
              placeholder={g.service_wanted_placeholder}
              className="rounded-xl border-brand/20 bg-white focus-visible:ring-brand min-h-[80px]"
            />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-brand-ink font-semibold">{g.coupon_label}</Label>
            <div className="flex gap-2">
              <Input
                data-testid="g-coupon"
                value={couponCode}
                onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponState({ status: "idle", label: null, reason: null }); }}
                placeholder="CFM-XXXX"
                className="rounded-xl border-brand/20 bg-white focus-visible:ring-brand h-11 font-mono"
              />
              <Button type="button" onClick={checkCoupon} disabled={!couponCode.trim() || couponState.status === "checking"} variant="outline" className="rounded-xl border-brand/25 text-brand font-bold h-11 shrink-0" data-testid="g-coupon-check">
                {g.coupon_check}
              </Button>
            </div>
            {couponState.status === "valid" && (
              <p className="mt-1.5 text-xs font-bold text-emerald-700 flex items-center gap-1" data-testid="g-coupon-ok">
                <Check className="h-3.5 w-3.5" /> {g.coupon_applied.replace("{label}", couponState.label)}
              </p>
            )}
            {couponState.status === "invalid" && (
              <p className="mt-1.5 text-xs font-semibold text-rose-600" data-testid="g-coupon-error">{couponState.reason}</p>
            )}
          </div>
          <div className="sm:col-span-2">
            <Button data-testid="grooming-submit-btn" type="submit" disabled={loading} className="w-full rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-12 text-base no-a11y-underline">
              <PawPrint className="h-4 w-4 mr-2" />
              {loading ? g.submitting : g.submit}
            </Button>
          </div>
        </motion.form>
      </div>
    </section>
  );
};
